import React from 'react';
import PropTypes from 'prop-types';
// Models
import mInstallationLog from 'models/installationLog';
import mScenario from 'models/scenario';
// HOCs
import wrapListFilters from 'hocs/WrapListFilters';
// Custom
import { listFeatureSearchFieldShape, listFilterShape } from 'constants/PropTypesShapes';
import { toIntegerOrNull } from 'utils/Tools';
import { dateGenerator } from 'utils/DateUtils';
// Components
import FiltersTemplate from 'components/templates/FiltersTemplate';
import BaseListFilter from 'components/filters/BaseListFilter';
import DateFilter from 'components/filters/DateFilter';
import ItemsSelectFieldFilter from 'components/filters/ItemsSelectFieldFilter';
import TextFieldFilter from 'components/filters/TextFieldFilter';
import CustomerAutoCompleteFilter from 'installations/assets/customers/CustomerAutoCompleteFilter';
import ScenarioSelectFieldFilter from 'installations/assets/scenarios/ScenarioSelectFieldFilter';
import TriggerSelectFieldFilter from 'installations/assets/configuration/TriggerSelectFieldFilter';
import EntitySelectFieldFilter from 'installations/assets/entities/EntitySelectFieldFilter';

const LogFilters = ({
  id,
  timezone,
  disabled,
  advanced,
  nbFiltersApplied,
  searchFields,
  field,
  filters,
  isFieldDisplayable,
  onFieldChange,
  onFilterFieldChange,
  onReinit,
  onSwitch,
}) => {
  const {
    begin, end, type, result, desc, installationCustomer, entityId, scenarioId, scenarioStatus, triggerId, scenarioTriggerType,
  } = filters;
  // Dates
  const beginDate = dateGenerator.dateFromTimezone(begin.value, timezone);
  const endDate = dateGenerator.dateFromTimezone(end.value, timezone);

  return (
    <FiltersTemplate
      disabled={disabled}
      advanced={advanced}
      nbFiltersApplied={nbFiltersApplied}
      onReinit={onReinit}
      onSwitch={onSwitch}
    >
      <div className="row m-0 pb-3">
        {!advanced && (
          <div className="col-12 col-md-4 col-xl-3">
            <BaseListFilter
              field={field}
              searchFields={searchFields}
              onChange={onFieldChange}
            />
          </div>
        )}
        {isFieldDisplayable('begin') && (
          <div className="col-12 col-md-4 col-xl-3">
            <DateFilter
              labelId="field.afterDate"
              type="begin"
              value={beginDate}
              onChange={(value) => onFilterFieldChange('begin', value)}
            />
          </div>
        )}
        {isFieldDisplayable('end') && (
          <div className="col-12 col-md-4 col-xl-3">
            <DateFilter
              labelId="field.beforeDate"
              type="end"
              value={endDate}
              onChange={(value) => onFilterFieldChange('end', value)}
            />
          </div>
        )}
        {isFieldDisplayable('type') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ItemsSelectFieldFilter
              labelId="field.type"
              value={type.value}
              items={mInstallationLog.typesList}
              onChange={(value) => onFilterFieldChange('type', value)}
            />
          </div>
        )}
        {isFieldDisplayable('result') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ItemsSelectFieldFilter
              labelId="field.result"
              value={result.value}
              items={mInstallationLog.resultsList}
              onChange={(value) => onFilterFieldChange('result', value)}
            />
          </div>
        )}
        {isFieldDisplayable('scenarioStatus') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ItemsSelectFieldFilter
              labelId="field.execScenario"
              value={scenarioStatus.value}
              items={mScenario.logStatusList}
              onChange={(value) => onFilterFieldChange('scenarioStatus', value)}
            />
          </div>
        )}
        {isFieldDisplayable('scenarioId') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ScenarioSelectFieldFilter
              scenarioId={toIntegerOrNull(scenarioId.value)}
              onChange={(value) => onFilterFieldChange('scenarioId', value)}
            />
          </div>
        )}
        {isFieldDisplayable('desc') && (
          <div className="col-12 col-md-4 col-xl-3">
            <TextFieldFilter
              label="field.accessWay"
              value={desc.value}
              onChange={(value) => onFilterFieldChange('desc', value)}
            />
          </div>
        )}
        {isFieldDisplayable('installationCustomer') && (
          <div className="col-12 col-md-4 col-xl-3">
            <CustomerAutoCompleteFilter
              id={id}
              customer={installationCustomer.value}
              onSelect={(value) => onFilterFieldChange('installationCustomer', value)}
            />
          </div>
        )}
        {isFieldDisplayable('entityId') && (
          <div className="col-12 col-md-4 col-xl-3">
            <EntitySelectFieldFilter
              entityId={toIntegerOrNull(entityId.value)}
              onChange={(value) => onFilterFieldChange('entityId', value)}
            />
          </div>
        )}
        {isFieldDisplayable('triggerId') && (
          <div className="col-12 col-md-4 col-xl-3">
            <TriggerSelectFieldFilter
              triggerId={toIntegerOrNull(triggerId.value)}
              onChange={(value) => onFilterFieldChange('triggerId', value)}
            />
          </div>
        )}
        {isFieldDisplayable('scenarioTriggerType') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ItemsSelectFieldFilter
              labelId="field.accessType"
              value={scenarioTriggerType.value}
              items={mScenario.triggerTypesList}
              onChange={(value) => onFilterFieldChange('scenarioTriggerType', value)}
            />
          </div>
        )}
      </div>
    </FiltersTemplate>
  );
};

LogFilters.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timezone: PropTypes.string.isRequired,
  // From HOC
  disabled: PropTypes.bool.isRequired,
  advanced: PropTypes.bool.isRequired,
  nbFiltersApplied: PropTypes.number.isRequired,
  searchFields: PropTypes.arrayOf(listFeatureSearchFieldShape).isRequired,
  field: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    begin: listFilterShape,
    end: listFilterShape,
    type: listFilterShape,
    result: listFilterShape,
    desc: listFilterShape,
    installationCustomer: listFilterShape,
    entityId: listFilterShape,
    scenarioId: listFilterShape,
    scenarioStatus: listFilterShape,
    triggerId: listFilterShape,
    scenarioTriggerType: listFilterShape,
  }).isRequired,
  isFieldDisplayable: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onFilterFieldChange: PropTypes.func.isRequired,
  onReinit: PropTypes.func.isRequired,
  onSwitch: PropTypes.func.isRequired,
};

module.exports = wrapListFilters(
  mInstallationLog.listFeatures.searchFields,
  {
    getInstallationCustomerParams: {
      id: (ownProps) => (ownProps.id),
    },
  },
)(LogFilters);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/logs/LogFilters.js